import { IStairCalculationResult, IStairConfig, IStairType, WOOD_MATERIALS } from '@stair-craft/common/util';

import {
    EDxfEntityKind,
    EDxfHorizontalAlign,
    EDxfVerticalAlign,
    IDxfEntity,
    IDxfLine,
    IDxfPoint,
    IDxfText,
    textWidth,
} from './dxf-entities';
import { EDxfSheetLayer } from './dxf-layers';
import { toDxfText } from './dxf-text';

/*
 * The main inscription of ГОСТ 2.104, form 1: 185 × 55 mm in the bottom-right corner of the
 * frame. Left — the signature columns (Изм., Лист, № докум., Подп., Дата) over the rows Разраб.,
 * Пров., Т.контр., Н.контр., Утв.; right — the designation on top, the name and the material
 * under it, the letter, mass and scale cells, the sheet count and the organisation.
 */

/** Width and height of the form, millimetres. */
export const TITLE_BLOCK_SIZE: { width: number; height: number } = { width: 185, height: 55 };
/** The brand the drawing is issued by — a name, not translated. */
export const TITLE_BLOCK_ORGANISATION: string = 'GoldenCalc';

const SMALL_TEXT_MM: number = 2.5;
const TEXT_MM: number = 3.5;
const NAME_TEXT_MM: number = 5;
/** Space kept between a cell's text and its rules, millimetres. */
const CELL_PAD_MM: number = 1;

/** What the inscription says, every text in the page language. */
export interface ITitleBlockData {
    name: string;
    /** The second line of the name: the kind of turn, if the stair has one. */
    subtitle: string | null;
    /** The designation field: the floor height, the step count, the riser and tread. */
    designation: string[];
    /** The material field: the species of every part, a line per group. */
    material: string[];
    scale: string;
    /** ДД.ММ.ГГ — the day the file was written. */
    date: string;
}

interface ICell {
    x0: number;
    y0: number;
    x1: number;
    y1: number;
}

const STAIR_NAMES: Record<IStairType, () => string> = {
    straight: () => $localize`:@@dxfNameStraight:Лестница прямая`,
    'l-shape': () => $localize`:@@dxfNameLShape:Лестница Г-образная`,
    'u-shape': () => $localize`:@@dxfNameUShape:Лестница П-образная`,
    spiral: () => $localize`:@@dxfNameSpiral:Лестница винтовая`,
};

function woodName(id: string): string {
    return WOOD_MATERIALS.find((wood: { id: string }): boolean => wood.id === id)?.name ?? id;
}

/** «Ступени: Дуб» — a part and the species it is cut from. */
function partLine(part: string, materialId: string): string {
    return `${part}: ${woodName(materialId)}`;
}

function materialLines(config: IStairConfig): string[] {
    const treads: string = partLine($localize`:@@labelTreads:Ступени`, config.treadMaterialId);
    const handrail: string = partLine($localize`:@@labelHandrailMaterial:Поручни`, config.handrailMaterialId);
    const balusters: string = partLine($localize`:@@labelBalusterMaterial:Балясины и столбы`, config.balusterMaterialId);
    const railing: string[] = config.railing.enabled ? [`${handrail}; ${balusters}`] : [];
    if (config.type === 'spiral') {
        return [treads, ...railing];
    }
    const stringer: string =
        config.stringerType === 'kosour' ? $localize`:@@stringerTypeKosour:Косоур` : $localize`:@@stringerTypeTetiva:Тетива`;
    const risers: string = partLine($localize`:@@labelRisers:Подступенки`, config.riserMaterialId);
    return [`${treads}; ${risers}`, partLine(stringer, config.stringerMaterialId), ...railing];
}

function twoDigits(value: number): string {
    return String(value).padStart(2, '0');
}

/** ДД.ММ.ГГ, the date form of the inscription. */
export function titleBlockDate(date: Date): string {
    return `${twoDigits(date.getDate())}.${twoDigits(date.getMonth() + 1)}.${twoDigits(date.getFullYear() % 100)}`;
}

/** The inscription's texts for a calculation; `denominators` are the scales of the views drawn at one. */
export function titleBlockData(
    result: IStairCalculationResult,
    config: IStairConfig,
    denominators: readonly number[],
    date: Date
): ITitleBlockData {
    const distinct: number[] = [...new Set(denominators.map((denominator: number): number => Math.round(denominator * 100) / 100))];
    const turns: boolean = config.type === 'l-shape' || config.type === 'u-shape';
    const turn: string =
        config.turnType === 'landing' ? $localize`:@@turnTypeLanding:Площадка` : $localize`:@@turnTypeWinder:Забежные ступени`;
    const mm: string = $localize`:@@dxfUnitMm:мм`;
    const floorHeight: string = $localize`:@@dxfKeyFloorHeight:Высота этажа`;
    const steps: string = $localize`:@@resultTileSteps:Ступеней`;
    const riserTread: string = $localize`:@@dxfKeyRiserTread:Подъём × проступь`;
    const riser: string = String(Math.round(result.riserHeight));
    const tread: string = String(Math.round(result.treadDepth));
    return {
        name: STAIR_NAMES[config.type](),
        subtitle: turns ? turn : null,
        designation: [
            `${floorHeight} ${String(config.floorHeight)} ${mm}, ${steps} ${String(result.stepCount)}`,
            `${riserTread} ${riser} × ${tread} ${mm}`,
        ],
        material: materialLines(config),
        scale: distinct.length === 1 ? `1:${String(distinct[0])}` : $localize`:@@dxfStampSeeViews:см. виды`,
        date: titleBlockDate(date),
    };
}

/** A text inside a cell, squeezed to its width; `rows`/`row` set it on one of several lines, top first. */
function cellText(text: string, cell: ICell, height: number, align: EDxfHorizontalAlign, line: { rows: number; row: number }): IDxfText {
    const value: string = toDxfText(text);
    const room: number = cell.x1 - cell.x0 - 2 * CELL_PAD_MM;
    const widthFactor: number = Math.min(1, room / Math.max(textWidth(value, height), 1e-9));
    const rowHeight: number = (cell.y1 - cell.y0) / line.rows;
    const y: number = cell.y1 - rowHeight * (line.row + 0.5);
    const x: number = align === EDxfHorizontalAlign.Left ? cell.x0 + CELL_PAD_MM : (cell.x0 + cell.x1) / 2;
    return {
        kind: EDxfEntityKind.Text,
        layer: EDxfSheetLayer.Text,
        ltScale: 1,
        at: [x, y],
        text: value,
        rotationDeg: 0,
        hAlign: align,
        vAlign: EDxfVerticalAlign.Middle,
        height,
        widthFactor,
    };
}

function one(text: string, cell: ICell, height: number, align: EDxfHorizontalAlign = EDxfHorizontalAlign.Center): IDxfText {
    return cellText(text, cell, height, align, { rows: 1, row: 0 });
}

function lines(texts: readonly string[], cell: ICell, height: number, rows: number = texts.length): IDxfText[] {
    return texts.map((text: string, row: number): IDxfText => cellText(text, cell, height, EDxfHorizontalAlign.Left, { rows, row }));
}

/** The rules of the form, in its own millimetres: main lines on the frame layer, row rules thin. */
function main(from: IDxfPoint, to: IDxfPoint): IDxfLine {
    return { kind: EDxfEntityKind.Line, ltScale: 1, layer: EDxfSheetLayer.Frame, from, to };
}

function thin(from: IDxfPoint, to: IDxfPoint): IDxfLine {
    return { kind: EDxfEntityKind.Line, ltScale: 1, layer: EDxfSheetLayer.Thin, from, to };
}

function formRules(): IDxfLine[] {
    return [
        main([0, 0], [185, 0]),
        main([185, 0], [185, 55]),
        main([185, 55], [0, 55]),
        main([0, 55], [0, 0]),
        // The signature columns: Изм. and Лист over the changes and the header row, the rest full height.
        main([7, 30], [7, 55]),
        ...[17, 40, 55, 65].map((x: number): IDxfLine => main([x, 0], [x, 55])),
        main([0, 30], [65, 30]),
        main([0, 35], [65, 35]),
        ...[5, 10, 15, 20, 25, 40, 45, 50].map((y: number): IDxfLine => thin([0, y], [65, y])),
        // The designation over the name and the material; the letter, mass and scale cells; the sheets row.
        main([65, 40], [185, 40]),
        main([65, 15], [185, 15]),
        main([135, 0], [135, 40]),
        main([135, 35], [185, 35]),
        main([135, 20], [185, 20]),
        main([150, 20], [150, 40]),
        main([167, 20], [167, 40]),
        main([155, 15], [155, 20]),
        thin([140, 20], [140, 35]),
        thin([145, 20], [145, 35]),
    ];
}

function cell(x0: number, y0: number, x1: number, y1: number): ICell {
    return { x0, y0, x1, y1 };
}

function signatureTexts(data: ITitleBlockData): IDxfText[] {
    const header: [string, number, number][] = [
        [$localize`:@@dxfStampChange:Изм.`, 0, 7],
        [$localize`:@@dxfStampSheet:Лист`, 7, 17],
        [$localize`:@@dxfStampDocNo:№ докум.`, 17, 40],
        [$localize`:@@dxfStampSign:Подп.`, 40, 55],
        [$localize`:@@dxfStampDate:Дата`, 55, 65],
    ];
    const rows: [string, number][] = [
        [$localize`:@@dxfStampDrawn:Разраб.`, 25],
        [$localize`:@@dxfStampChecked:Пров.`, 20],
        [$localize`:@@dxfStampTechControl:Т.контр.`, 15],
        [$localize`:@@dxfStampNormControl:Н.контр.`, 5],
        [$localize`:@@dxfStampApproved:Утв.`, 0],
    ];
    return [
        ...header.map(([text, x0, x1]: [string, number, number]): IDxfText => one(text, cell(x0, 30, x1, 35), SMALL_TEXT_MM)),
        ...rows.map(([text, y0]: [string, number]): IDxfText =>
            one(text, cell(0, y0, 17, y0 + 5), SMALL_TEXT_MM, EDxfHorizontalAlign.Left)
        ),
        one(TITLE_BLOCK_ORGANISATION, cell(17, 25, 40, 30), SMALL_TEXT_MM, EDxfHorizontalAlign.Left),
        one(data.date, cell(55, 25, 65, 30), SMALL_TEXT_MM),
    ];
}

function fieldTexts(data: ITitleBlockData): IDxfText[] {
    const sheetLabel: string = $localize`:@@dxfStampSheet:Лист`;
    const sheetsLabel: string = $localize`:@@dxfStampSheets:Листов`;
    const name: IDxfText[] =
        data.subtitle === null
            ? [one(data.name, cell(65, 15, 135, 40), NAME_TEXT_MM)]
            : [
                  cellText(data.name, cell(65, 15, 135, 40), NAME_TEXT_MM, EDxfHorizontalAlign.Center, { rows: 2, row: 0 }),
                  cellText(data.subtitle, cell(65, 15, 135, 40), TEXT_MM, EDxfHorizontalAlign.Center, { rows: 2, row: 1 }),
              ];
    return [
        ...lines(data.designation, cell(65, 40, 185, 55), TEXT_MM),
        ...name,
        ...lines(data.material, cell(65, 0, 135, 15), SMALL_TEXT_MM, 3),
        one($localize`:@@dxfStampLetter:Лит.`, cell(135, 35, 150, 40), SMALL_TEXT_MM),
        one($localize`:@@dxfStampMass:Масса`, cell(150, 35, 167, 40), SMALL_TEXT_MM),
        one($localize`:@@dxfStampScale:Масштаб`, cell(167, 35, 185, 40), SMALL_TEXT_MM),
        one(data.scale, cell(167, 20, 185, 35), TEXT_MM),
        one(`${sheetLabel} 1`, cell(135, 15, 155, 20), SMALL_TEXT_MM, EDxfHorizontalAlign.Left),
        one(`${sheetsLabel} 1`, cell(155, 15, 185, 20), SMALL_TEXT_MM, EDxfHorizontalAlign.Left),
        one(TITLE_BLOCK_ORGANISATION, cell(135, 0, 185, 15), NAME_TEXT_MM),
    ];
}

/** The inscription as paper-space entities with its bottom-left corner at `at`, millimetres. */
export function titleBlockEntities(data: ITitleBlockData, at: IDxfPoint): IDxfEntity[] {
    const local: IDxfEntity[] = [...formRules(), ...signatureTexts(data), ...fieldTexts(data)];
    return local.map((entity: IDxfEntity): IDxfEntity => {
        if (entity.kind === EDxfEntityKind.Line) {
            return { ...entity, from: [entity.from[0] + at[0], entity.from[1] + at[1]], to: [entity.to[0] + at[0], entity.to[1] + at[1]] };
        }
        return entity.kind === EDxfEntityKind.Text ? { ...entity, at: [entity.at[0] + at[0], entity.at[1] + at[1]] } : entity;
    });
}
